import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getMoodFoods, logMood, getMoodFood, getMoodActivities, toggleMoodActivity, getMoodWellness } from '../lib/api';
import MoodInsights from '../components/mood/MoodInsights';
import { trackEvent } from '../lib/firebase';
import { Loader2, ChevronLeft, ChevronRight, Check, Wind, Beaker } from 'lucide-react';

const moodColors = {
  happy: '#FFD93D', stressed: '#6BCB77', tired: '#4D96FF', anxious: '#9B59B6',
  'low-energy': '#E74C3C', focused: '#3498DB', bloated: '#1ABC9C',
  'craving-sweet': '#FF6B9D', angry: '#E67E22', grateful: '#E91E63'
};

const moodEmojis = {
  happy: '😊', stressed: '😤', tired: '😴', anxious: '😰',
  'low-energy': '🔋', focused: '🎯', bloated: '🤢',
  'craving-sweet': '🍬', angry: '😠', grateful: '🙏'
};

export default function MoodPage() {
  const { deviceId } = useAuth();
  const [moods, setMoods] = useState([]);
  const [selectedMood, setSelectedMood] = useState(null);
  const [moodDetail, setMoodDetail] = useState(null);
  const [activities, setActivities] = useState(null);
  const [wellness, setWellness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [logging, setLogging] = useState(false);
  const [view, setView] = useState('log'); // log | insights | detail

  useEffect(() => {
    async function load() {
      try {
        const moodsRes = await getMoodFoods();
        setMoods(moodsRes.data || []);
      } catch {}
      setLoading(false);
    }
    load();
  }, []);

  const handleLogMood = async (moodId) => {
    setLogging(true);
    try {
      await logMood({ device_id: deviceId, mood_id: moodId });
      trackEvent('mood_log', { mood_id: moodId });
      setSelectedMood(moodId);
      setWellness(null);
      // Load food recommendation, activities, and wellness data
      try {
        const [foodRes, actRes, wellRes] = await Promise.allSettled([
          getMoodFood(moodId, deviceId),
          getMoodActivities(moodId, deviceId),
          getMoodWellness(moodId),
        ]);
        if (foodRes.status === 'fulfilled') setMoodDetail(foodRes.value.data);
        if (actRes.status === 'fulfilled') setActivities(actRes.value.data);
        if (wellRes.status === 'fulfilled') setWellness(wellRes.value.data);
      } catch {}
      setView('detail');
    } catch {}
    setLogging(false);
  };

  const handleToggleActivity = async (activityId, completed) => {
    try {
      await toggleMoodActivity({ device_id: deviceId, mood_id: selectedMood, activity_id: activityId, completed: !completed });
      const res = await getMoodActivities(selectedMood, deviceId);
      setActivities(res.data);
    } catch {}
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--primary)' }} />
    </div>
  );

  return (
    <div className="p-4 sm:p-6 md:p-8 lg:p-12 max-w-5xl mx-auto" data-testid="mood-page">
      {/* Tab Navigation */}
      <div className="flex gap-2 sm:gap-3 mb-6 sm:mb-8 flex-wrap">
        <button onClick={() => setView('log')} data-testid="mood-tab-log"
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${view === 'log' ? 'shadow-sm' : ''}`}
          style={{ background: view === 'log' ? 'var(--primary)' : 'var(--surface)', color: view === 'log' ? 'var(--primary-fg)' : 'var(--text)', border: `1px solid ${view === 'log' ? 'var(--primary)' : 'var(--border)'}` }}>
          Log Mood
        </button>
        <button onClick={() => setView('insights')} data-testid="mood-tab-insights"
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${view === 'insights' ? 'shadow-sm' : ''}`}
          style={{ background: view === 'insights' ? 'var(--primary)' : 'var(--surface)', color: view === 'insights' ? 'var(--primary-fg)' : 'var(--text)', border: `1px solid ${view === 'insights' ? 'var(--primary)' : 'var(--border)'}` }}>
          Insights
        </button>
        {selectedMood && (
          <button onClick={() => setView('detail')} data-testid="mood-tab-detail"
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${view === 'detail' ? 'shadow-sm' : ''}`}
            style={{ background: view === 'detail' ? 'var(--primary)' : 'var(--surface)', color: view === 'detail' ? 'var(--primary-fg)' : 'var(--text)', border: `1px solid ${view === 'detail' ? 'var(--primary)' : 'var(--border)'}` }}>
            Recommendations
          </button>
        )}
      </div>

      {/* Log Mood View */}
      {view === 'log' && (
        <div className="animate-fade-in">
          <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>How are you feeling today?</h2>
          <p className="mb-8 text-sm" style={{ color: 'var(--muted-fg)' }}>Select the mood that best describes you right now</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
            {moods.map((m, i) => (
              <button key={m.id} onClick={() => handleLogMood(m.id)} disabled={logging}
                className={`p-5 rounded-lg border text-center transition-all hover:-translate-y-1 hover:shadow-lg animate-fade-in stagger-${i + 1}`}
                style={{ background: selectedMood === m.id ? `${moodColors[m.id]}20` : 'var(--surface)', borderColor: selectedMood === m.id ? moodColors[m.id] : 'var(--border)' }}
                data-testid={`mood-btn-${m.id}`}>
                <span className="text-3xl block mb-2">{moodEmojis[m.id] || '😊'}</span>
                <span className="text-sm font-medium" style={{ color: 'var(--text)' }}>{m.mood}</span>
              </button>
            ))}
          </div>
          {logging && (
            <div className="flex items-center justify-center mt-6 gap-2" style={{ color: 'var(--primary)' }}>
              <Loader2 className="w-5 h-5 animate-spin" /> <span className="text-sm">Logging mood...</span>
            </div>
          )}
        </div>
      )}

      {/* Mood Detail / Recommendations */}
      {view === 'detail' && selectedMood && (
        <div className="animate-fade-in space-y-6">
          <button onClick={() => setView('log')} className="flex items-center gap-1 text-sm" style={{ color: 'var(--primary)' }}>
            <ChevronLeft className="w-4 h-4" /> Back to moods
          </button>

          <div className="rounded-lg p-6 border" style={{ background: `${moodColors[selectedMood]}15`, borderColor: moodColors[selectedMood] }}>
            <div className="flex items-center gap-3 mb-2">
              <span className="text-3xl">{moodEmojis[selectedMood]}</span>
              <div>
                <h2 className="text-xl font-bold capitalize" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>{selectedMood.replace('-', ' ')}</h2>
                <p className="text-sm" style={{ color: 'var(--muted-fg)' }}>Logged just now</p>
              </div>
            </div>
          </div>

          {/* Food Recommendation */}
          {moodDetail && (
            <div className="rounded-lg p-6 border" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }} data-testid="mood-food-detail">
              <h3 className="font-bold mb-3" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>
                Food Recommendation
              </h3>
              <h4 className="text-lg font-semibold mb-1" style={{ color: 'var(--text)' }}>
                {moodDetail.food_name || moodDetail.name || 'Recommended meal'}
              </h4>
              <p className="text-sm mb-3" style={{ color: 'var(--muted-fg)' }}>
                {moodDetail.description || moodDetail.benefit || ''}
              </p>
              {moodDetail.recipe && (
                <div className="mt-3 p-4 rounded-lg" style={{ background: 'var(--bg)' }}>
                  <p className="text-xs uppercase tracking-widest font-semibold mb-2" style={{ color: 'var(--muted-fg)', fontFamily: 'Manrope' }}>Recipe</p>
                  <p className="text-sm whitespace-pre-line" style={{ color: 'var(--text)' }}>{typeof moodDetail.recipe === 'string' ? moodDetail.recipe : JSON.stringify(moodDetail.recipe, null, 2)}</p>
                </div>
              )}
              {moodDetail.nutrients && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {(Array.isArray(moodDetail.nutrients) ? moodDetail.nutrients : []).map((n, i) => (
                    <span key={i} className="text-xs px-2 py-1 rounded" style={{ background: 'var(--muted)', color: 'var(--muted-fg)' }}>{n}</span>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Activities */}
          {activities && activities.activities && (
            <div className="rounded-lg p-6 border" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }} data-testid="mood-activities">
              <h3 className="font-bold mb-4" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>Suggested Activities</h3>
              <div className="space-y-3">
                {activities.activities.map((act) => (
                  <button key={act.id} onClick={() => handleToggleActivity(act.id, act.completed)}
                    className="w-full flex items-center gap-3 p-3 rounded-lg transition-all hover:shadow-sm text-left"
                    style={{ background: act.completed ? 'var(--primary)' + '10' : 'var(--bg)', border: `1px solid ${act.completed ? 'var(--primary)' : 'var(--border)'}` }}
                    data-testid={`activity-${act.id}`}>
                    <div className="w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0"
                      style={{ borderColor: act.completed ? 'var(--primary)' : 'var(--border)', background: act.completed ? 'var(--primary)' : 'transparent' }}>
                      {act.completed && <Check className="w-3.5 h-3.5 text-white" />}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium" style={{ color: 'var(--text)' }}>{act.name || act.title}</p>
                      {act.description && <p className="text-xs" style={{ color: 'var(--muted-fg)' }}>{act.description}</p>}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Body Chemistry */}
          {wellness?.body_chemistry && (
            <div className="rounded-lg p-6 border" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }} data-testid="body-chemistry">
              <div className="flex items-center gap-2 mb-4">
                <Beaker className="w-5 h-5" style={{ color: '#9B59B6' }} />
                <h3 className="font-bold" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>{wellness.body_chemistry.title}</h3>
              </div>
              <div className="space-y-3 mb-4">
                {wellness.body_chemistry.chemicals.map((chem, i) => (
                  <div key={i} className="flex items-start gap-3 p-3 rounded-lg" style={{ background: 'var(--bg)' }}>
                    <span className="text-lg shrink-0">{chem.emoji}</span>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-0.5">
                        <p className="font-bold text-sm" style={{ color: 'var(--text)' }}>{chem.name}</p>
                        <span className="text-xs px-2 py-0.5 rounded capitalize"
                          style={{
                            background: chem.level === 'high' || chem.level === 'elevated' || chem.level === 'spiking' ? '#EF444420' :
                                       chem.level === 'low' ? '#3B82F620' :
                                       chem.level === 'optimal' || chem.level === 'moderate' ? '#10B98120' : '#F59E0B20',
                            color: chem.level === 'high' || chem.level === 'elevated' || chem.level === 'spiking' ? '#EF4444' :
                                   chem.level === 'low' ? '#3B82F6' :
                                   chem.level === 'optimal' || chem.level === 'moderate' ? '#10B981' : '#F59E0B',
                          }}>
                          {chem.level}
                        </span>
                      </div>
                      <p className="text-xs" style={{ color: 'var(--muted-fg)' }}>{chem.description}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="p-3 rounded-lg" style={{ background: `${moodColors[selectedMood]}10`, border: `1px solid ${moodColors[selectedMood]}30` }}>
                <p className="text-xs font-medium" style={{ color: 'var(--text)' }}>{wellness.body_chemistry.tip}</p>
              </div>
            </div>
          )}

          {/* Breathing Technique */}
          {wellness?.breathing && (() => {
            const bt = wellness.breathing;
            return (
              <div className="rounded-lg p-6 border" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }} data-testid="breathing-technique">
                <div className="flex items-center gap-2 mb-4">
                  <Wind className="w-5 h-5" style={{ color: bt.color }} />
                  <h3 className="font-bold" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>Breathing Technique</h3>
                </div>
                <div className="rounded-lg p-4 mb-4" style={{ background: `${bt.color}10`, border: `1px solid ${bt.color}25` }}>
                  <h4 className="font-bold text-base mb-1" style={{ fontFamily: 'Manrope', color: bt.color }}>{bt.name}</h4>
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-xs font-medium px-2 py-0.5 rounded" style={{ background: `${bt.color}20`, color: bt.color }}>{bt.pattern}</span>
                    <span className="text-xs" style={{ color: 'var(--muted-fg)' }}>{bt.duration}</span>
                  </div>
                  <p className="text-sm" style={{ color: 'var(--text)' }}>{bt.description}</p>
                </div>
                <div className="space-y-2">
                  {bt.steps.map((step, i) => (
                    <div key={i} className="flex items-start gap-2.5">
                      <div className="w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold shrink-0 mt-0.5"
                        style={{ background: `${bt.color}20`, color: bt.color }}>
                        {i + 1}
                      </div>
                      <p className="text-sm" style={{ color: 'var(--text)' }}>{step}</p>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* Insights View */}
      {view === 'insights' && (
        <MoodInsights />
      )}
    </div>
  );
}
